import React, { useState, useRef, useCallback, useEffect } from "react";
import { Loader2, MoreVertical } from "lucide-react";
import * as analyticsApi from "features/analytics/studio/api/analyticsApi";
import DrawerTab from "shared/components/ui/DrawerTab";

function getEmbedVideoUrl(url) {
  if (!url) return null;
  const u = url.trim();
  const yt = u.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/i);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vm = u.match(/(?:vimeo\.com\/|player\.vimeo\.com\/video\/)(\d+)/i);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return null;
}

/**
 * Media card for board v2. All colors via CSS design tokens.
 */
export default function MediaBoardCard({ card, boardId, homeWorkspaceId, onSaveUrl, onSaveDisplayOptions, onDelete, readOnly }) {
  const q = card?.queryDsl && typeof card.queryDsl === "object" ? card.queryDsl : {};
  const url = q.url || "";
  const mediaType = q.type || "image";
  const [tab, setTab] = useState("upload");
  const [embedUrl, setEmbedUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [menuOpen]);

  const processFile = useCallback(async (file) => {
    if (!file || (!boardId && !homeWorkspaceId) || !onSaveUrl) return;
    if (file.type.startsWith("video/")) { setUploadError("Use Embed Video Link for YouTube or Vimeo."); return; }
    if (!file.type.startsWith("image/")) { setUploadError("Please upload an image."); return; }
    setUploadError(null);
    setUploading(true);
    try {
      const data = homeWorkspaceId
        ? await analyticsApi.uploadHomeWorkspaceMedia(homeWorkspaceId, file)
        : await analyticsApi.uploadBoardMedia(boardId, file);
      if (data?.url) onSaveUrl(data.url, "image");
    } catch (err) {
      setUploadError(err?.response?.data?.message || err?.message || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }, [boardId, homeWorkspaceId, onSaveUrl]);

  const handleDrop = useCallback((e) => {
    e.preventDefault(); setIsDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;
    if (file.type.startsWith("video/")) { setUploadError("Use Embed Video Link for YouTube or Vimeo."); return; }
    processFile(file);
  }, [processFile]);

  const embedVideoUrl = getEmbedVideoUrl(url);
  const isVideo = mediaType === "video" || url.match(/\.(mp4|webm)(\?|$)/i);

  return (
    <div className="group/media relative w-full h-full flex flex-col min-h-0 overflow-hidden">
      {/* ⋮ menu */}
      {!readOnly && onDelete && (
        <div ref={menuRef} className="absolute right-2 top-2 z-10">
          <button
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            className="p-1 rounded-md text-[color:var(--text-muted)] opacity-0 group-hover/media:opacity-60 hover:!opacity-100 hover:bg-surface-hover transition-all"
          >
            <MoreVertical className="w-4 h-4" />
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-full mt-1 overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface py-1 shadow-token-lg z-20 min-w-[120px]">
              <button
                type="button"
                onClick={() => { setMenuOpen(false); onDelete(); }}
                className="w-full px-3 py-2 text-left text-[12px] text-[color:var(--feedback-error)] transition-colors hover:bg-[color:color-mix(in_srgb,var(--feedback-error)_6%,transparent)]"
                style={{ fontFamily: "Inter, sans-serif" }}
              >
                Delete
              </button>
            </div>
          )}
        </div>
      )}

      {url ? (
        <div className="flex-1 min-h-0 flex items-center justify-center p-2 overflow-hidden">
          {embedVideoUrl ? (
            <iframe
              src={embedVideoUrl}
              title={card?.title || "Video"}
              className="w-full h-full rounded"
              style={{ border: "none", aspectRatio: "16/9", maxHeight: "100%", maxWidth: "100%" }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : isVideo ? (
            <video src={url} controls className="max-h-full max-w-full rounded" style={{ display: "block" }} />
          ) : (
            <img
              src={url}
              alt={card?.title || "Media"}
              className="max-h-full max-w-full rounded object-contain"
              style={{ display: "block" }}
            />
          )}
        </div>
      ) : !readOnly && (boardId || homeWorkspaceId) && onSaveUrl ? (
        <div className="flex-1 min-h-0 flex flex-col items-center justify-center p-4">
          {/* Upload / Embed tab switcher */}
          <div className="flex gap-1 mb-4">
            <DrawerTab id="upload" label="Upload Image" active={tab === "upload"} onClick={setTab} />
            <DrawerTab id="embed" label="Embed Video" active={tab === "embed"} onClick={setTab} />
          </div>

          {tab === "upload" ? (
            <div
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
              onDrop={handleDrop}
              onClick={() => !uploading && fileInputRef.current?.click()}
              className="w-full max-w-xs flex flex-col items-center justify-center py-8 border-2 border-dashed rounded-md cursor-pointer transition-colors"
              style={{
                borderColor: isDragging ? "var(--brand-primary)" : "var(--border-default)",
                backgroundColor: isDragging ? "color-mix(in srgb, var(--brand-primary) 8%, transparent)" : "transparent",
              }}
            >
              <input ref={fileInputRef} type="file" accept="image/*" onChange={(e) => processFile(e.target.files?.[0])} className="hidden" />
              {uploading ? (
                <>
                  <Loader2 className="w-6 h-6 animate-spin mb-2 text-[color:var(--brand-primary)]" />
                  <p className="text-[12px] text-[color:var(--text-muted)]">Uploading…</p>
                </>
              ) : (
                <>
                  <p className="text-[12px] font-medium text-[color:var(--brand-primary)]">Choose file</p>
                  <p className="text-[11px] mt-1 text-[color:var(--text-muted)]">or drag and drop</p>
                </>
              )}
              {uploadError && (
                <p className="text-[11px] mt-2 text-[color:var(--feedback-error)]">{uploadError}</p>
              )}
            </div>
          ) : (
            <input
              type="url"
              value={embedUrl}
              onChange={(e) => setEmbedUrl(e.target.value)}
              onBlur={() => { const v = embedUrl.trim(); if (v && onSaveUrl) onSaveUrl(v, "video"); }}
              placeholder="Paste YouTube or Vimeo URL"
              className="w-full max-w-xs h-8 rounded-md border border-[color:var(--border-default)] bg-surface px-3 text-[12px] text-[color:var(--text-primary)] outline-none placeholder:text-[color:var(--text-muted)] focus:border-[color:var(--brand-primary)] transition-colors"
              style={{ fontFamily: "Inter, sans-serif" }}
            />
          )}
        </div>
      ) : (
        <div className="flex-1 min-h-0 flex items-center justify-center">
          <p className="text-[13px] text-[color:var(--text-muted)]" style={{ fontFamily: "Inter, sans-serif" }}>No media</p>
        </div>
      )}

      {/* Drag handle overlay */}
      {!readOnly && (
        <div
          className="board-drag-handle absolute left-1 top-2 flex items-center justify-center opacity-0 group-hover/media:opacity-40 transition-opacity cursor-grab active:cursor-grabbing z-10 select-none text-[color:var(--text-muted)]"
          style={{ width: 16 }}
        >
          <svg width="10" height="14" viewBox="0 0 10 14" fill="currentColor">
            <circle cx="2.5" cy="2.5" r="1.5" /><circle cx="7.5" cy="2.5" r="1.5" />
            <circle cx="2.5" cy="7"   r="1.5" /><circle cx="7.5" cy="7"   r="1.5" />
            <circle cx="2.5" cy="11.5" r="1.5" /><circle cx="7.5" cy="11.5" r="1.5" />
          </svg>
        </div>
      )}
    </div>
  );
}
